import React, { useMemo } from "react";
import { Calendar } from "lucide-react";
import { SimpleTooltip, Pagination, TabSelector } from "../../common";
import {
  DataTable,
  DataTableCard,
} from "../../common/Table2";
import type { TableColumn } from "../../common/Table";
import type { CompOffItem } from "../../../store/apis/compOff.api";
import Badge from "../../common/Badge";
import { getCompOffStatusVariant } from "../../../utils/badgeVariants";
import { capitalizeWords } from "../../../utils/nameUtils";
import { getWorkingPeriodLabel } from "../../../utils/compOff";
import { TimesheetManagementSkeleton } from "../../timesheet/Skeleton";
import { HEADER_CLS } from "../../../utils/constants";
import { COMP_OFF_STATUS_FILTER_OPTIONS } from "../../../utils/compOff";
import { formatDateDDMMMYYYY } from "../../../utils/timeUtils";

export type CompOffStatusFilter = "all" | "pending" | "approved" | "rejected";

interface LeaveTablePaginationProps {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  itemsPerPageOptions?: number[];
}

interface LeaveTableSectionProps {
  title?: string;
  requests: CompOffItem[];
  loading?: boolean;
  onViewDetails: (request: CompOffItem) => void;
  onPullback: (request: CompOffItem) => void;
  canPullback: (request: CompOffItem) => boolean;
  getStatusLabel: (request: CompOffItem) => string;
  userTimezone: string;
  statusFilter?: CompOffStatusFilter;
  onStatusFilterChange?: (value: CompOffStatusFilter) => void;
  pagination?: LeaveTablePaginationProps;
}

const CompensatoryLeaveTable: React.FC<LeaveTableSectionProps> = ({
  title = "Comp-Off History",
  requests,
  loading = false,
  onViewDetails,
  onPullback,
  getStatusLabel,
  userTimezone,
  statusFilter,
  onStatusFilterChange,
  pagination,
}) => {
  const sortedRequests = useMemo(
    () =>
      [...requests].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [requests],
  );

  const columns = useMemo<TableColumn<CompOffItem>[]>(() => {
    return [
      {
        key: "fullName",
        title: "Name",
        label: "Name",
        required: true,
        headerClassName: HEADER_CLS,
        render: (_, request) => (
          <span className="min-w-[140px] text-label text-textColor-primary whitespace-nowrap capitalize">
            {request.fullName || "—"}
          </span>
        ),
      },
      {
        key: "leaveDate",
        title: "Leave Date",
        label: "Leave Date",
        headerClassName: HEADER_CLS,
        render: (_, request) => (
          <span className="text-sm text-textColor-secondary whitespace-nowrap">
            {formatDateDDMMMYYYY(request.leaveDate, userTimezone)}
          </span>
        ),
      },
      {
        key: "workingPeriod",
        title: "Working Period",
        label: "Working Period",
        headerClassName: HEADER_CLS,
        render: (_, request) => (
          <span className="text-sm text-textColor-secondary">
            {getWorkingPeriodLabel(request.isFirstHalf, request.isSecondHalf)}
          </span>
        ),
      },
      {
        key: "comment",
        title: "Comment",
        label: "Comment",
        headerClassName: HEADER_CLS,
        align: "left",
        truncate: false,
        render: (_, request) => (
          <div className="flex items-center w-36">
            <SimpleTooltip label={request.comment} side="top">
              <span className="text-sm text-textColor-secondary max-w-36 block overflow-hidden text-ellipsis whitespace-nowrap ">
                {request.comment}
              </span>
            </SimpleTooltip>
          </div>
        ),
      },
      {
        key: "status",
        title: "Status",
        label: "Status",
        headerClassName: HEADER_CLS,
        render: (_, request) => (
          <Badge
            variant={getCompOffStatusVariant(request?.status)}
            size="middle"
            className={request?.status?.toLowerCase() === "pending" ? "pr-4 sm:pr-2.5" : ""}
          >
            {request.statusLabel ?? capitalizeWords(request?.status)}
          </Badge>
        ),
      },
      {
        key: "appliedDate",
        title: "Applied Date",
        label: "Applied Date",
        headerClassName: HEADER_CLS,
        render: (_, request) => <span className="text-sm text-textColor-secondary whitespace-nowrap">{formatDateDDMMMYYYY(request.createdAt, userTimezone)}</span>,
      },
    ];
  }, [
    getStatusLabel,
    userTimezone,
    onPullback,
    onViewDetails,
  ]);

  return (
    <DataTableCard
      title={title}
      toolbar={
        statusFilter && onStatusFilterChange && (
          <TabSelector
            tabs={COMP_OFF_STATUS_FILTER_OPTIONS.map(opt => ({ k: opt.value, label: opt.label }))}
            active={statusFilter}
            onChange={(val) => onStatusFilterChange(val as any)}
            variant="segmented"
            size="sm"
          />
        )
      }
      pagination={
        pagination && pagination.totalItems > 0 ? (
          <Pagination
            currentPage={pagination.currentPage}
            totalItems={pagination.totalItems}
            itemsPerPage={pagination.itemsPerPage}
            onPageChange={pagination.onPageChange}
            onItemsPerPageChange={
              pagination.onItemsPerPageChange
                ? pagination.onItemsPerPageChange
                : (_value: number) => undefined
            }
            itemsPerPageOptions={
              pagination.itemsPerPageOptions || [5, 10, 20, 50, 100]
            }
          />
        ) : null
      }
    >
      <DataTable<CompOffItem>
        columns={columns}
        rows={sortedRequests}
        loading={loading}
        skeleton={<TimesheetManagementSkeleton rows={5} />}
        emptyMessage="No leave requests found"
        rowKey={(r) => r.id}
      />
    </DataTableCard>
  );
};

export default CompensatoryLeaveTable;
